import classNames from "classnames";
import { useSynchronizedRef } from "client/Hooks";
import {
	isNilOrWhiteSpace,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	Context,
	createContext,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	ReadonlyRefObject,
	useContext,
	useMemo
} from "react";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { IconButton, IconButtonKind } from "../IconButton/IconButton";
import { FontIconName } from "../Icon/Icon";
import {
	ToolbarAlignmentContext,
	ToolbarAlignmentContextData
} from "../Toolbar/ToolbarAlignmentContext";
import styles from "./ResponsiveMenu.module.less";

export type ResponsiveMenuProps = {
	trackingId: string | undefined;
	height?: "auto" | "fill";
	hasBusyIndicator?: boolean;
	onClose: (() => void) | undefined;
	onMouseDown?: (args: MouseEvent) => void;
};

export const ResponsiveMenu = (props: PropsWithChildren<ResponsiveMenuProps>): ReactElement => {
	const {
		trackingId,
		height = "auto",
		hasBusyIndicator = false,
		onClose: onCloseFromProps,
		onMouseDown,
		children
	} = props;

	const onCloseRef: ReadonlyRefObject<(() => void) | undefined> = useSynchronizedRef(onCloseFromProps);
	const hasOnClose: boolean = isNotNil(onCloseRef.current);

	const responsiveMenuContext = useMemo(
		(): ResponsiveMenuContextData => ({
			onClose: hasOnClose
				? () => onCloseRef.current?.()
				: undefined
		}),
		[hasOnClose, onCloseRef]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.menu,
				{ [styles.menu_fill]: height === "fill" }
			)}
			onMouseDown={onMouseDown}
		>
			{hasBusyIndicator && (
				<BusyIndicator />
			)}

			<ResponsiveMenuContext.Provider value={responsiveMenuContext}>
				{children}
			</ResponsiveMenuContext.Provider>
		</div>
	);
};
ResponsiveMenu.displayName = "ResponsiveMenu";

type ResponsiveMenuHeaderAction = {
	trackingId: string;
	icon: FontIconName;
	onClick: () => void;
	kind: IconButtonKind;
};

export type ResponsiveMenuHeaderProps = {
	title: string;
	actions?: ResponsiveMenuHeaderAction[];
};

const ResponsiveMenuHeader = (props: ResponsiveMenuHeaderProps): ReactElement => {
	const {
		title,
		actions
	} = props;

	const { onClose } = useContext(ResponsiveMenuContext);

	return (
		<header
			{...setTrackingId("header")}
			className={styles.header}
		>
			<h2
				{...setTrackingId("title")}
				className={styles.title}
			>
				{isNilOrWhiteSpace(title)
					? localize("_NoSubject")
					: title
				}
			</h2>
			<div className={styles.headerActions}>
				{actions && actions.map(
					(x: ResponsiveMenuHeaderAction): ReactElement => (
						<IconButton
							size="large"
							kind={x.kind}
							shape="round"
							icon={x.icon}
							onClick={x.onClick}
							trackingId={x.trackingId}
							key={x.trackingId}
						/>
					)
				)}
				{onClose && (
					<IconButton
						icon="xmark"
						size="large"
						onClick={onClose}
						trackingId="close-button"
						kind="secondary"
						shape="round"
					/>
				)}
			</div>
		</header>
	);
};
ResponsiveMenuHeader.displayName = "ResponsiveMenu.Header";

export type ResponsiveMenuBodyProps = {
	goesEdgeToEdge?: boolean;
};

const ResponsiveMenuBody = (props: PropsWithChildren<ResponsiveMenuBodyProps>): ReactElement => {
	const {
		goesEdgeToEdge = false,
		children
	} = props;

	return (
		<div
			{...setTrackingId("body")}
			className={classNames(
				styles.body,
				{ [styles.body_edgeToEdge]: goesEdgeToEdge }
			)}
		>
			{children}
		</div>
	);
};
ResponsiveMenuBody.displayName = "ResponsiveMenu.Body";

export const ResponsiveMenuFooter = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<footer
			{...setTrackingId("footer")}
			className={styles.footer}
		>
			<ToolbarAlignmentContext.Provider value={responsiveMenuFooterToolbarAlignment}>
				{children}
			</ToolbarAlignmentContext.Provider>
		</footer>
	);
};
ResponsiveMenuFooter.displayName = "ResponsiveMenu.Footer";

const responsiveMenuFooterToolbarAlignment: ToolbarAlignmentContextData = {
	horizontalAlignment: "right",
	dropDownDirection: "up"
};

ResponsiveMenu.Header = ResponsiveMenuHeader;
ResponsiveMenu.Body = ResponsiveMenuBody;
ResponsiveMenu.Footer = ResponsiveMenuFooter;

type ResponsiveMenuContextData = {
	onClose: (() => void) | undefined;
};

const ResponsiveMenuContext: Context<ResponsiveMenuContextData> = createContext<ResponsiveMenuContextData>({
	onClose: undefined
});
